import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { applyTextStyle, responsiveText } from '../theme/typography'; // Componentes de tipografia pré-definidos

const TypographyBase = styled.div`
  margin: 0;
  padding: 0;
  font-family: inherit;
`;
export const Display = styled(TypographyBase)`
  ${applyTextStyle('display')}
`;
export const H1 = styled(TypographyBase)`
  ${applyTextStyle('h1')}
  margin-bottom: ${props => props.noMargin ? '0' : '1.5rem'};
`;
export const H2 = styled(TypographyBase)`
  ${applyTextStyle('h2')}
  margin-bottom: ${props => props.noMargin ? '0' : '1.25rem'};
`;
export const H3 = styled(TypographyBase)`
  ${applyTextStyle('h3')}
  margin-bottom: ${props => props.noMargin ? '0' : '1rem'};
`;
export const H4 = styled(TypographyBase)`
  ${applyTextStyle('h4')}
  margin-bottom: ${props => props.noMargin ? '0' : '0.875rem'};
`;
export const H5 = styled(TypographyBase)`
  ${applyTextStyle('h5')}
  margin-bottom: ${props => props.noMargin ? '0' : '0.75rem'};
`;
export const H6 = styled(TypographyBase)`
  ${applyTextStyle('h6')}
  margin-bottom: ${props => props.noMargin ? '0' : '0.625rem'};
`;
export const Body = styled(TypographyBase)`
  ${applyTextStyle('body')}
  margin-bottom: ${props => props.noMargin ? '0' : '1rem'};
`;
export const BodyLarge = styled(TypographyBase)`
  ${applyTextStyle('bodyLarge')}
  margin-bottom: ${props => props.noMargin ? '0' : '1rem'};
`;
export const BodySmall = styled(TypographyBase)`
  ${applyTextStyle('bodySmall')}
  margin-bottom: ${props => props.noMargin ? '0' : '0.75rem'};
`;
export const Caption = styled(TypographyBase)`
  ${applyTextStyle('caption')}
  margin-bottom: ${props => props.noMargin ? '0' : '0.5rem'};
`;
export const Lead = styled(TypographyBase)`
  ${applyTextStyle('lead')}
  margin-bottom: ${props => props.noMargin ? '0' : '1.25rem'};
`; // Componente de texto responsivo

export const ResponsiveText = styled(TypographyBase)`
  ${props => responsiveText(props.mobile, props.tablet, props.desktop)}
  margin-bottom: ${props => props.noMargin ? '0' : '1rem'};
`;
ResponsiveText.propTypes = {
  mobile: PropTypes.string.isRequired,
  tablet: PropTypes.string,
  desktop: PropTypes.string,
  noMargin: PropTypes.bool
}; // Componente de texto customizado

export const Text = styled(TypographyBase)`
  font-size: ${props => props.size || 'inherit'};
  font-weight: ${props => props.weight || 'inherit'};
  line-height: ${props => props.lineHeight || 'inherit'};
  letter-spacing: ${props => props.letterSpacing || 'inherit'};
  text-align: ${props => props.align || 'inherit'};
  color: ${props => props.color || 'inherit'};
  margin-bottom: ${props => props.noMargin ? '0' : props.marginBottom || '1rem'};
  
  ${props => props.uppercase && 'text-transform: uppercase;'}
  ${props => props.lowercase && 'text-transform: lowercase;'}
  ${props => props.capitalize && 'text-transform: capitalize;'}
  ${props => props.italic && 'font-style: italic;'}
  ${props => props.underline && 'text-decoration: underline;'}
  ${props => props.strike && 'text-decoration: line-through;'}
`;
Text.propTypes = {
  size: PropTypes.string,
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lineHeight: PropTypes.string,
  letterSpacing: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right', 'justify']),
  color: PropTypes.string,
  marginBottom: PropTypes.string,
  noMargin: PropTypes.bool,
  uppercase: PropTypes.bool,
  lowercase: PropTypes.bool,
  capitalize: PropTypes.bool,
  italic: PropTypes.bool,
  underline: PropTypes.bool,
  strike: PropTypes.bool
}; // Componente de exemplo para demonstrar o sistema

export const TypographyExample = () => <div style={{
  padding: '2rem',
  maxWidth: '800px'
}}>
    <H1>Tipografia Padronizada</H1>
    <Lead>Este é um exemplo do novo sistema de tipografia implementado na aplicação.</Lead>
    
    <H2>Hierarquia de Títulos</H2>
    <H1>H1 - Título Principal (36px)</H1>
    <H2>H2 - Subtítulo (32px)</H2>
    <H3>H3 - Seção (28px)</H3>
    <H4>H4 - Subseção (24px)</H4>
    <H5>H5 - Item (20px)</H5>
    <H6>H6 - Subitem (18px)</H6>
    
    <H3>Textos do Corpo</H3>
    <BodyLarge>BodyLarge - Texto grande (16px)</BodyLarge>
    <Body>Body - Texto padrão (14px)</Body>
    <BodySmall>BodySmall - Texto pequeno (12px)</BodySmall>
    <Caption>Caption - Legenda (10px)</Caption>
    
    <H3>Textos Responsivos</H3>
    <ResponsiveText mobile="bodySmall" tablet="body" desktop="bodyLarge">
      Este texto se adapta ao tamanho da tela
    </ResponsiveText>
    
    <H3>Textos Customizados</H3>
    <Text size="18px" weight="600" color="#233980">
      Texto customizado com cor da marca
    </Text>
    <Text size="16px" weight="500" uppercase color="#e81920">
      Texto em maiúsculas com cor de destaque
    </Text>
    <Text size="14px" italic color="#6b6b6b">
      Texto em itálico para citações
    </Text>
    
    <H3>Classes Utilitárias</H3>
    <p className="text-2xl font-bold">Classe: text-2xl font-bold</p>
    <p className="text-lg font-medium text-blue-600">Classe: text-lg font-medium</p>
    <p className="text-sm leading-relaxed">Classe: text-sm leading-relaxed</p>
  </div>;
export default {
  Display,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  Body,
  BodyLarge,
  BodySmall,
  Caption,
  Lead,
  ResponsiveText,
  Text,
  TypographyExample
};